/**
 * useResolvedUserId
 * ------------------
 * Returns the canonical user_id used by Vision Board / Living Creation flows.
 *
 * Resolution order:
 *   1. Authenticated user's email (`user.email` from useAuth)  — preferred
 *   2. localStorage["userId"]                                 — anonymous fallback
 *   3. Newly-generated "user_xxx"                              — last resort
 *
 * On login transition (anon -> authenticated), this hook fires a one-time
 * `POST /api/vision-board/claim` so any visions saved while anonymous are
 * re-keyed to the user's email and become visible everywhere they sign in.
 *
 * Drop-in replacement for the previous `localStorage.getItem('userId')` pattern.
 */
import { useEffect, useMemo, useRef } from 'react';
import { useAuth } from './useAuth';

const STORAGE_KEY = 'userId';
const CLAIM_FLAG = 'reclaim_claimed_for';

const generateAnonId = () => 'user_' + Math.random().toString(36).slice(2, 11);

const ensureAnonId = () => {
  let id = localStorage.getItem(STORAGE_KEY);
  if (!id) {
    id = generateAnonId();
    localStorage.setItem(STORAGE_KEY, id);
  }
  return id;
};

export const useResolvedUserId = () => {
  const auth = useAuth?.() || {};
  const user = auth.user || null;
  const email = user?.email || null;

  // Compute the resolved id once per render. If logged in -> email, else anon.
  const resolved = useMemo(() => {
    if (email) return email;
    return ensureAnonId();
  }, [email]);

  // On first render where we have an email but localStorage["userId"]
  // still holds an anonymous id, fire the claim once and pin the email.
  const claimedRef = useRef(false);
  useEffect(() => {
    if (!email || claimedRef.current) return;
    const previousAnon = localStorage.getItem(STORAGE_KEY);
    const alreadyClaimedFor = localStorage.getItem(CLAIM_FLAG);

    // Always pin the email so future reads of "userId" return it.
    localStorage.setItem(STORAGE_KEY, email);

    // If we never claimed for this email, and there was a different anon id, migrate.
    if (
      previousAnon &&
      previousAnon !== email &&
      previousAnon.startsWith('user_') &&
      alreadyClaimedFor !== email
    ) {
      claimedRef.current = true;
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/vision-board/claim`;
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ anon_user_id: previousAnon, claim_user_id: email }),
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          localStorage.setItem(CLAIM_FLAG, email);
          if (data && (data.favorites_migrated || data.immersions_migrated)) {
            // eslint-disable-next-line no-console
            console.info('[Reclaim] migrated anonymous visions:', data);
          }
        })
        .catch((err) => {
          // Non-fatal — try again next mount
          // eslint-disable-next-line no-console
          console.warn('[Reclaim] vision claim failed:', err);
          claimedRef.current = false;
        });
    } else {
      // Nothing to claim; mark so we don't re-attempt.
      localStorage.setItem(CLAIM_FLAG, email);
      claimedRef.current = true;
    }
  }, [email]);

  return resolved;
};

export default useResolvedUserId;
